/* ModelState — the reference component's `state` literal (design-reference/PointFive OS Original.dc.html, script lines 2–41),
 * typed. Key order and initial values are verbatim; only the type annotations are added. */
import type { InvestorGrant, InvestorPayment, InvestorTerms } from '@pointfive/contracts';
import type { DrillSpec, TipSpec } from './overlays';

export type ModeId = 'prepare' | 'execute' | 'analyze' | 'review' | 'manage';
export type ScreenId =
  | 'dashboard'
  | 'widgets'
  | 'briefing'
  | 'prepare'
  | 'riskrules'
  | 'riskprofile'
  | 'strategies'
  | 'tradehub'
  | 'signals'
  | 'trades'
  | 'copytrading'
  | 'analytics'
  | 'aiinsights'
  | 'pfscore'
  | 'journal'
  | 'replay'
  | 'eod'
  | 'acctprogress'
  | 'compareaccts'
  | 'tradingintel'
  | 'perfdigest'
  | 'investors'
  | 'tradingidentity'
  | 'billing'
  | 'accounts'
  | 'notifications'
  | 'settings';
export interface OpenPosition {
  /** The server's position id — what close/modify address. Rows are only ever built from server positions
   *  (authoritative read-back), so the id is always present on a real row. */
  id: string;
  /** The owning account, straight from the server row — the account dimension (§7) filters the display by it. */
  accountId: string;
  sym: string;
  type: string;
  vol: string;
  open: string;
  cur: string;
  sl: string;
  tp: string;
  pnl: string;
  pnlFg: string;
}
export interface TiRule {
  id: string;
  text: string;
  on: boolean;
  sys: boolean;
}
export interface PfHover {
  k: string;
  id: string;
  a: number;
  r: number;
}
export interface PfGoal {
  target: number;
  date: string;
}
export interface BacktestRun {
  strat: string;
  window: string;
  risk: number;
  months: number;
  cap: number;
  days: number;
  n: number;
  wins: number;
  losses: number;
  netR: number;
  expectancy: number;
  wr: number;
  pf: number;
  avgWin: number;
  avgLoss: number;
  maxDD: number;
  worstStreak: number;
  curve: { i: number; eq: number }[];
}
export type ToastKind = 'pos' | 'neg' | 'mut';

export interface ModelState {
  screen: ScreenId;
  mode: ModeId;
  ruleOn: Record<string, boolean>;
  rc: Record<string, boolean>;
  rcOpen: string | null;
  rpTab: string;
  mood: string;
  energy: number;
  focus: string[];
  tf: string;
  ticketTab: string;
  riskPct: string;
  posTab: string;
  posOpen: boolean;
  sideOpen: boolean;
  stratOn: Record<string, boolean>;
  armed: string[];
  preNote: string;
  tradeRange: string;
  briefTab: string;
  composerOpen: boolean;
  aiTab: string;
  trQ: string;
  trSide: string;
  trStrat: string;
  trAcct: string;
  trSort: string;
  trDir: number;
  trSel: number[];
  trPage: number;
  menu: string | null;
  sgScan: string;
  sgConf: number;
  sgDismissed: string[];
  wlErr: string;
  cpConnected: boolean;
  cpTab: string;
  cpFollow: string[];
  cpModal: string | null;
  cpMult: number;
  cpMaxCopies: number;
  cpCopySlTp: boolean;
  cpStrats: string[];
  ordSide: string;
  ordLots: string;
  ordSl: string;
  ordTp: string;
  ordSlOn: boolean;
  ordTpOn: boolean;
  /** The instrument the desk is working — the one the chart shows, the ticket prices against, and an order is
   *  placed on. The reference hardcoded XAUUSD and told the trader charts "are not wired in this prototype". */
  deskSym: string;
  deskQ: string;
  deskFav: string[];
  deskFavOnly: boolean;
  /** Arming is in flight. The button disables itself rather than letting a second press open a second session. */
  armPending: boolean;
  /** The server's answer to "am I armed?", held so the screen has one source for it rather than its own guess. */
  sessionState: string;
  sessionId: string | null;
  ordModal: boolean;
  ordErr: string;
  /** In flight through the funnel — the desk is disabled while a dispatch is out, because a second click is a second
   *  order and the first has not answered yet. */
  ordSubmitting: boolean;
  /** The idempotency key of a dispatch that never came back. Held so the screen can say "we are checking", and so it
   *  can confirm-by-read later — never so it can re-send (REQ-LADDER). */
  ordUnconfirmed: string | null;
  openPos: OpenPosition[];
  /** An in-flight position operation ('close-<id>' | 'closeAll' | 'modify-<id>') — buttons disable while set,
   *  because a second click on a money action is a second order (REQ-LADDER at the UI). */
  posBusy: string | null;
  /** The modify editor's draft, or null when closed. */
  posModify: { id: string; sym: string; sl: string; tp: string; decimals: number } | null;
  /** The close confirmation, or null. */
  posCloseConfirm: { id: string; sym: string; vol: string; pnl: string } | null;
  posCloseAllConfirm: boolean;
  /** THE order draft (mandate §5): snapshotted when the confirm modal opens, rendered by the modal, submitted as
   *  the payload, echoed by the toast. One object end to end, so the ticket, the confirmation and the order can
   *  never disagree about what is being traded. */
  signOutPending: boolean;
  /** Lock Day (SDD §8.1): the confirm dialog's state, and the lock the session knows it placed. */
  lockDay: { closeAll: boolean; busy: boolean } | null;
  dayLockedUntil: string | null;
  /** The daily brief (mandate §16): the trader's stated intentions for the day. Client-side journal-layer
   *  state, like preNote — the ENFORCED caps live in the Risk Controls and ride prepare.session.arm. */
  briefGoal: string;
  briefMaxLoss: string;
  briefTarget: string;
  briefLots: string;
  /** Authoritative risk state (mandate §1): the server's profile values, rule states and queued weakenings —
   *  what the Prepare cards and Risk Rules screen render AND what the funnel enforces. Local `rc`/`ruleOn`
   *  are dead the moment this loads; they remain only as the pre-load skeleton. */
  risk: {
    loaded: boolean;
    busy: string | null; // the control key currently saving, for per-card busy states
    error: string | null;
    values: Record<string, string>;
    rules: { key: string; enabled: boolean; params: Record<string, string>; effectiveAt: string | null }[];
    pending: { id: string; key: string; from: string; to: string; effectiveAt: string }[];
    frozen: boolean;
    editor: { key: string; name: string; value: string } | null;
    /** Trader-authored advisory rules (mandate §5) — the catalogue's `custom` list, rendered after the
     *  system cards. */
    custom: {
      id: string;
      label: string;
      expression: string;
      severity: 'soft' | 'hard';
      enabled: boolean;
      lastFiredAt: string | null;
    }[];
    /** Rule param editor — the RiskRules row's Edit, writing riskRules.update({params}). */
    ruleEditor: { key: string; name: string; params: { k: string; v: string }[] } | null;
    /** Add Rule dialog draft (advisory custom rule). */
    customEditor: { label: string; expression: string; severity: 'soft' | 'hard' } | null;
  } | null;
  /** Dashboard widget visibility (mandate §5: the Widgets screen's Edit Dashboard) — the server catalogue
   *  with per-user visibility; dialog true while the edit sheet is open. */
  dashW: {
    loaded: boolean;
    items: { key: string; label: string; visible: boolean }[];
    dialog: boolean;
  } | null;
  /** The strategies playbook (mandate §5): server rows behind the reference cards' toggles and the
   *  data-driven created cards; editor is the New Strategy / edit dialog draft. */
  strat: {
    loaded: boolean;
    busy: boolean;
    items: {
      id: string;
      name: string;
      status: 'active' | 'paused' | 'archived';
      rules: string[];
      sessions: string[];
      stats: { trades: number; winRate: number; expectancyR: number };
    }[];
    editor: { id: string | null; name: string; sessions: string; rules: string } | null;
  } | null;
  /** Settings profile draft (mandate §16): loaded from me.profile.get, saved via me.profile.update. */
  profile: {
    name: string;
    handle: string;
    email: string;
    loaded: boolean;
    busy: boolean;
    error: string | null;
  } | null;
  /** Global filters (mandate §6): one range preset and one symbol, broadcast to every consumer named in
   *  model/global-filters.ts. 'all' is the neutral default for both; the chips read these. */
  gFilterRange: 'day' | 'week' | 'month' | 'year' | 'all';
  gFilterSym: string;
  gFilterOpen: 'range' | 'symbols' | null;
  gFilterSymQuery: string;
  /** TradeHub account dimension (mandate §7): null = the armed session's own account (ADR-016's rule), else an
   *  explicit selection the ticket and the positions view follow together. */
  thAcct: string | null;
  thAcctOpen: boolean;
  /** Positions-table controls (mandate §9). */
  posFilterSym: string;
  posPage: number;
  deskExtra: string[];
  deskAddOpen: boolean;
  deskAddInput: string;
  /** Account linking (mandate §1): the composed modal's whole state. Null = closed. */
  acctLink: {
    tab: 'link' | 'manage';
    step: 'provider' | 'form' | 'verify' | 'done';
    providerId: string | null;
    params: Record<string, string>;
    /** Secret fields (passwords) — held only while the dialog is open, sent write-only on verify. */
    secrets: Record<string, string>;
    nickname: string;
    busy: boolean;
    error: string | null;
    linkId: string | null;
    message: string | null;
    unlinkConfirm: { id: string; label: string } | null;
    health?: readonly { accountId: string; sync: string; broker: string }[];
  } | null;
  /** Investors (mandate §14): the fetched grants + the working surfaces. The list is FETCHED, never assumed —
   *  grants are mutation-shaped state the composed dataset does not carry. */
  inv: {
    loading: boolean;
    error: string | null;
    grants: readonly InvestorGrant[];
    payments: readonly InvestorPayment[];
    form: { name: string; email: string; expires: string };
    busy: boolean;
    formError: string | null;
    /** The one-time link from a fresh grant — the ONLY moment the plaintext exists client-side. */
    issued: { grantId: string; token: string | null; name: string } | null;
    revokeConfirm: { id: string; name: string } | null;
    terms: {
      grantId: string;
      name: string;
      draft: InvestorTerms;
      busy: boolean;
    } | null;
  } | null;
  /** What the server last said the profile was — the diff base for partial updates. */
  profileOriginal: { name: string; handle: string } | null;
  ordDraft: {
    sym: string;
    side: 'buy' | 'sell';
    lots: number;
    px: number;
    sl: number | null;
    tp: number | null;
    account: string;
  } | null;
  toast: string;
  toastKind: string;
  /** The entrance ramp, 0 → 1 over 1.6s on first mount. Every meter is drawn at `value × intro`, so the figures grow
   *  from zero rather than appearing at full length. Reduced motion lands on 1 immediately. */
  intro: number;
  /** The figure tooltip that follows the pointer, or null. */
  tip: TipSpec | null;
  /** The detail drawer's contents, or null when it is closed. */
  drill: DrillSpec | null;
  /** Which calendar day the Trading-day drawer is showing (`null` = closed), and that day's P&L. */
  dayKey: string | null;
  dayVal: number;
  /** The day drawer's AI verdict lifecycle. */
  dayAI: 'idle' | 'loading' | 'done';
  notifTab: string;
  setSection: string;
  notifPrefs: Record<string, boolean>;
  /** §4: true once me.preferences hydrated notifPrefs — the literals above are the pre-load skeleton. */
  prefsLoaded: boolean;
  jLevel: string;
  jYear: number;
  jMonth: number;
  jWeek: number | null;
  jPick: number;
  jShot: string;
  jAttach: string[];
  jShotModal: boolean;
  jPrepEdit: Record<string, string>;
  jPostEdit: Record<string, string>;
  jTagInput: string;
  jTagAdd: Record<string, string[]>;
  jSearch: string;
  jMoodFilter: string;
  /** Not in the reference state literal — jrnModel() reads `S.jGroupBy || 'none'` and sets it from the group tabs. */
  jGroupBy?: string;
  jDay: number;
  eodSel: number;
  apAcct: string;
  apImprove: boolean;
  anTab: string;
  anRange: string;
  anStrat: string;
  anSym: string;
  anHeatRange: string;
  anLogAcct: string[];
  cmpSel: string[];
  cmpPeriod: string;
  tiStyle: string;
  tiExp: string;
  tiMax: string;
  tiRR: string;
  tiRisk: string;
  tiSess: string[];
  tiInst: string[];
  tiGoals: string;
  tiDescText: string;
  tiDetected: string | null;
  tiSaved: string | null;
  tiRules: TiRule[] | null;
  tiRuleNew: string;
  tiRuleAdding: boolean;
  tiRuleEditId: string | null;
  tiRuleDraft: string;
  pfTab: string;
  pfPillar: string | null;
  pfShare: string | null;
  pfOptIn: boolean;
  pfTips: Record<string, string[]>;
  pfTipsLoading: string | null;
  aiPeriod: string;
  aiAcct: string;
  aiRunning: string | null;
  aiExpanded: number;
  aiStyleSet: boolean;
  pfGoal: PfGoal | null;
  pfGoalForm: boolean;
  pfGoalTarget: string;
  pfGoalDate: string;
  pfCopied: boolean;
  pfPublic: boolean;
  dgTab: string;
  dgWeekly: boolean;
  dgMonthly: boolean;
  dgDay: number;
  dgAi: boolean;
  dgLog: boolean;
  dgEquity: boolean;
  dgBusy: boolean;
  dgMade: Record<string, boolean>;
  jNote: Record<string, string>;
  jTagSel: string[];
  jModal: boolean;
  jfSym: string;
  jfSide: string;
  jfStrat: string;
  jfR: string;
  jfNote: string;
  jfErr: string;
  rpDay: number;
  rpTrade: number;
  rpBar: number;
  rpSpeed: number;
  rpTradesOpen: boolean;
  tvReady: boolean;
  rpMode: string;
  btResultsOpen: boolean;
  btStrat: string;
  btWindow: string;
  btRisk: number;
  btMonths: number;
  btCap: number;
  btRun: BacktestRun | null;
  btBusy: boolean;
  replayPlaying: boolean;
  theme: 'light' | 'dark';
  navOpen: boolean;
  acctOpen: boolean;
  avatarSrc: string;
  eqRange: string;
  mcAcct: string;
  mcRange: string;
  mcHover: number | null;
  calM: number;
  gRange: string;
  pfRange: string;
  pfSnap: number | null;
  pfMetric: string | null;
  pfHover: PfHover | null;
  wl: string[];
  /** §4: true once the server's watchlist hydrated S.wl — the initial five are the pre-load skeleton. */
  wlLoaded: boolean;
  wlInput: string;
  /** Not in the reference literal — read as `S.acctFilter || 'all'`; initialised to 'all' (equivalent). */
  acctFilter: string;
}

export const initialState: ModelState = {
  screen: 'dashboard',
  mode: 'prepare',
  ruleOn: { time: false, partial: true, trail: true, maxloss: false, comm: false, be1r: true },
  rc: {
    daily: true,
    weekly: false,
    ddcb: true,
    ptl: false,
    dtl: true,
    mps: true,
    mcp: false,
    slreq: true,
    cll: false,
    cooldown: false,
    news: false,
    window: false,
    revenge: true,
  },
  rcOpen: null,
  rpTab: 'controls',
  mood: 'neutral',
  energy: 6,
  focus: ['Patience', 'Risk Management'],
  tf: '1h',
  ticketTab: 'market',
  riskPct: '1%',
  posTab: 'positions',
  posOpen: true,
  sideOpen: true,
  stratOn: { ob: true, ldn: false },
  armed: ['London Open Reversal', 'Trend Continuation'],
  preNote: '',
  tradeRange: 'all',
  briefTab: 'day',
  composerOpen: false,
  aiTab: 'copilot',
  trQ: '',
  trSide: 'all',
  trStrat: 'all',
  trAcct: 'all',
  trSort: 'date',
  trDir: -1,
  trSel: [],
  trPage: 0,
  menu: null,
  sgScan: 'done',
  sgConf: 0,
  sgDismissed: [],
  wlErr: '',
  cpConnected: true,
  cpTab: 'providers',
  cpFollow: [],
  cpModal: null,
  cpMult: 1,
  cpMaxCopies: 3,
  cpCopySlTp: true,
  cpStrats: ['London Open Reversal', 'Trend Continuation', 'Breakout Pullback'],
  ordSide: 'buy',
  ordLots: '0.01',
  ordSl: '',
  ordTp: '',
  ordSlOn: true,
  ordTpOn: true,
  deskSym: 'XAUUSD',
  deskQ: '',
  deskFav: ['XAUUSD', 'EURUSD'],
  deskFavOnly: false,
  armPending: false,
  sessionState: 'none',
  sessionId: null,
  ordModal: false,
  ordSubmitting: false,
  ordUnconfirmed: null,
  ordErr: '',
  openPos: [],
  posBusy: null,
  posModify: null,
  posCloseConfirm: null,
  posCloseAllConfirm: false,
  signOutPending: false,
  lockDay: null,
  dayLockedUntil: null,
  briefGoal: '',
  briefMaxLoss: '',
  briefTarget: '',
  briefLots: '',
  risk: null,
  strat: null,
  dashW: null,
  profile: null,
  profileOriginal: null,
  gFilterRange: 'all',
  gFilterSym: 'all',
  gFilterOpen: null,
  gFilterSymQuery: '',
  thAcct: null,
  thAcctOpen: false,
  posFilterSym: '',
  posPage: 0,
  deskExtra: [],
  deskAddOpen: false,
  deskAddInput: '',
  acctLink: null,
  inv: null,
  ordDraft: null,
  intro: 0,
  tip: null,
  drill: null,
  dayKey: null,
  dayVal: 0,
  dayAI: 'idle',
  toast: '',
  toastKind: 'pos',
  notifTab: 'all',
  setSection: 'profile',
  notifPrefs: {
    riskAlerts: true,
    overtradingAlerts: true,
    lossLimitAlerts: true,
    tradeSuggestions: false,
    sessionReminders: true,
    performanceAlerts: true,
    systemMessages: false,
    automatedActions: true,
  },
  prefsLoaded: false,
  jLevel: 'year',
  jYear: 2026,
  jMonth: 6,
  jWeek: null,
  jPick: 0,
  jShot: 'pre-entry',
  jAttach: [],
  jShotModal: false,
  jPrepEdit: {},
  jPostEdit: {},
  jTagInput: '',
  jTagAdd: {},
  jSearch: '',
  jMoodFilter: 'all',
  jDay: 0,
  eodSel: 0,
  apAcct: 'ftmo',
  apImprove: false,
  anTab: 'overview',
  anRange: 'month',
  anStrat: 'all',
  anSym: 'all',
  anHeatRange: 'quarter',
  anLogAcct: ['Exness', 'FTMO', 'FundedNext'],
  cmpSel: ['Exness', 'FTMO', 'FundedNext'],
  cmpPeriod: 'month',
  tiStyle: 'day_trader',
  tiExp: 'advanced',
  tiMax: '4',
  tiRR: '1:2',
  tiRisk: '1',
  tiSess: ['London', 'New York', 'London/New York Overlap'],
  tiInst: ['Gold (XAU/USD)', 'Forex Majors', 'Indices (US30, NAS100, SPX500)'],
  tiGoals:
    'Pass the FundedNext 200K challenge without breaching the 5% daily loss limit, then run Exness and the funded accounts side by side at 1% risk. Full-time within two years.',
  tiDescText: '',
  tiDetected: null,
  tiSaved: null,
  tiRules: null,
  tiRuleNew: '',
  tiRuleAdding: false,
  tiRuleEditId: null,
  tiRuleDraft: '',
  pfTab: 'my-score',
  pfPillar: null,
  pfShare: null,
  pfOptIn: true,
  pfTips: {},
  pfTipsLoading: null,
  aiPeriod: 'today',
  aiAcct: 'all',
  aiRunning: null,
  aiExpanded: 0,
  aiStyleSet: true,
  pfGoal: null,
  pfGoalForm: false,
  pfGoalTarget: '850',
  pfGoalDate: '',
  pfCopied: false,
  pfPublic: false,
  dgTab: 'weekly',
  dgWeekly: true,
  dgMonthly: true,
  dgDay: 1,
  dgAi: true,
  dgLog: true,
  dgEquity: true,
  dgBusy: false,
  dgMade: { weekly: true, monthly: false },
  jNote: {},
  jTagSel: ['Patient', 'Followed plan'],
  jModal: false,
  jfSym: '',
  jfSide: 'LONG',
  jfStrat: 'London Open Reversal',
  jfR: '',
  jfNote: '',
  jfErr: '',
  rpDay: 0,
  rpTrade: 0,
  rpBar: 1284,
  rpSpeed: 1,
  rpTradesOpen: true,
  tvReady: false,
  rpMode: 'replay',
  btResultsOpen: true,
  btStrat: 'London Open Reversal',
  btWindow: '07:00–09:30',
  btRisk: 0.5,
  btMonths: 6,
  btCap: 2,
  btRun: null,
  btBusy: false,
  replayPlaying: false,
  theme: 'light',
  // ESC-004, owner ruling (directive 1.2): SDD §10.2 wins — the COLLAPSED rail is the default state at
  // 1024/768/390; the full sidebar is the default only at desktop width. The trader can expand it (the fold
  // control) and dismiss it again; Escape collapses an expanded rail at the narrow widths.
  navOpen: typeof window === 'undefined' ? true : window.innerWidth > 1024,
  acctOpen: false,
  avatarSrc: '',
  eqRange: 'week',
  mcAcct: 'global',
  mcRange: 'month',
  mcHover: null,
  calM: 6,
  gRange: 'month',
  pfRange: 'all',
  pfSnap: null,
  pfMetric: null,
  pfHover: null,
  wl: ['XAUUSD', 'EURUSD', 'GBPUSD', 'USDJPY', 'BTCUSD'],
  wlLoaded: false,
  wlInput: '',
  acctFilter: 'all',
};
